/**
 * `buildSceneItemInput` (WP-1).
 *
 * Pure helper that turns the Add-layer-to-gTwin dialog's form state into
 * the canonical `SceneItemInput` shape from `SCENE_PACKAGE_CONTRACT.md`
 * (mirrored locally in `sdk-stub/gtwin-sdk/scene-items.ts`). Designed to
 * be unit-testable end-to-end without a DOM: all five canonical artifact
 * kinds (COG, GLB, COPC, 3D Tiles, GeoJSON) and the additional ones
 * declared in `artifactContract.ts` are supported.
 *
 * Validation is delegated to `validateSceneItem` from the SDK so the
 * builder cannot diverge from the platform contract.
 */

import {
  ARTIFACT_SCENE_CONTRACT,
  type ArtifactKind,
  type ArtifactSceneContract,
  type SceneItemInput,
  type SceneItemValidationError,
  type Provenance,
  type ViewerUnsupportedBinaryVectorKind,
  validateSceneItem,
} from '@/sdk-stub/gtwin-sdk';

export interface AddLayerFormState {
  readonly artifactKind: ArtifactKind;
  readonly url: string;
  readonly title?: string;
  readonly sourceCrs?: string;
  readonly bbox?: readonly [number, number, number, number] | null;
  /** Optional override for the auto-generated id (`prefix-<timestamp>`). */
  readonly id?: string;
  readonly extensions?: Readonly<Record<string, unknown>>;
}

export interface BuildSceneItemResult {
  readonly input: SceneItemInput;
  readonly contract: ArtifactSceneContract;
  readonly errors: readonly SceneItemValidationError[];
}

const VIEWER_ACTOR = 'geoviewer-add-layer-dialog';

const DEFAULT_PROVENANCE: Provenance = {
  source: 'user-input',
  extractionMethod: VIEWER_ACTOR,
  confidence: 1,
  trusted: true,
  reviewStatus: 'approved',
};

function generateId(prefix: string, now = Date.now()): string {
  return `${prefix}-${now.toString(36)}`;
}

function trimOrUndefined(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Build a canonical `SceneItemInput` from the dialog form. The endpoint
 * key is taken from `ARTIFACT_SCENE_CONTRACT[kind].endpointKey` so the
 * server-side validator (`SCENE_ITEMS_SDK_SPEC.md` §2.4) accepts the
 * payload without normalisation.
 */
export function buildSceneItemInput(
  form: AddLayerFormState,
  options: { readonly now?: number; readonly actor?: string } = {},
): BuildSceneItemResult {
  const contract = ARTIFACT_SCENE_CONTRACT[form.artifactKind];
  if (!contract) {
    throw new Error(
      `buildSceneItemInput: unknown artifactKind "${String(form.artifactKind)}". ` +
        'Approved kinds live in artifactContract.ts.',
    );
  }
  const id = trimOrUndefined(form.id) ?? generateId(`layer-${contract.artifactKind}`, options.now);
  const url = form.url.trim();
  const provenance: Provenance = {
    ...DEFAULT_PROVENANCE,
    extractionMethod: options.actor ?? VIEWER_ACTOR,
  };

  const bbox = form.bbox
    ? ([form.bbox[0], form.bbox[1], form.bbox[2], form.bbox[3]] as [
        number,
        number,
        number,
        number,
      ])
    : undefined;

  const input: SceneItemInput = {
    id,
    artifactKind: contract.artifactKind as ArtifactKind,
    layerKind: contract.layerKind,
    slot: contract.slot,
    title: trimOrUndefined(form.title),
    endpoints: { [contract.endpointKey]: { href: url } },
    sourceCrs: trimOrUndefined(form.sourceCrs),
    bbox,
    provenance,
    extensions: form.extensions,
  };

  return {
    input,
    contract,
    errors: validateSceneItem(input),
  };
}

/** Convenience helper: throws when the canonical builder reports validation errors. */
export function buildSceneItemInputOrThrow(
  form: AddLayerFormState,
  options?: { readonly now?: number; readonly actor?: string },
): { readonly input: SceneItemInput; readonly contract: ArtifactSceneContract } {
  const built = buildSceneItemInput(form, options);
  if (built.errors.length > 0) {
    const summary = built.errors.map((error) => `${error.field}: ${error.message}`).join('; ');
    throw new Error(`buildSceneItemInput rejected: ${summary}`);
  }
  return { input: built.input, contract: built.contract };
}

/**
 * Catalog of dialog options grouped by user-facing label. Pulled from
 * `ARTIFACT_SCENE_CONTRACT` so adding a new artifactKind in the platform
 * automatically surfaces in the dialog.
 */
export interface ArtifactKindOption {
  readonly artifactKind: ArtifactKind | ViewerUnsupportedBinaryVectorKind;
  readonly label: string;
  readonly placeholder: string;
  readonly slot: ArtifactSceneContract['slot'];
}

export const ARTIFACT_KIND_OPTIONS: readonly ArtifactKindOption[] = [
  {
    artifactKind: 'cog',
    label: 'Raster — COG (.tif / .tiff)',
    placeholder: 'https://host/path/to/scene.cog.tif',
    slot: 'realityData',
  },
  {
    artifactKind: '3dtiles',
    label: '3D Tiles (tileset.json)',
    placeholder: 'https://host/path/to/tileset.json',
    slot: 'realityData',
  },
  {
    artifactKind: 'cityjson-3dtiles',
    label: '3D Tiles (CityJSON tileset.json)',
    placeholder: 'https://host/path/to/cityjson-tileset.json',
    slot: 'realityData',
  },
  {
    artifactKind: 'glb',
    label: 'BIM / Engineering model (.glb / .gltf)',
    placeholder: 'https://host/path/to/model.glb',
    slot: 'engineeringModels',
  },
  {
    artifactKind: 'copc',
    label: 'Point cloud — COPC (.copc.laz)',
    placeholder: 'https://host/path/to/cloud.copc.laz',
    slot: 'realityData',
  },
  {
    artifactKind: 'potree-v2-octree',
    label: 'Point cloud — Potree v2 (metadata.json)',
    placeholder: 'https://host/path/to/potree/metadata.json',
    slot: 'realityData',
  },
  {
    artifactKind: 'quantized-mesh',
    label: 'Terrain — Quantized Mesh (layer.json)',
    placeholder: 'https://host/path/to/layer.json',
    slot: 'terrain',
  },
  {
    artifactKind: 'geojson',
    label: 'Vector — GeoJSON',
    placeholder: 'https://host/path/to/features.geojson',
    slot: 'featureLayers',
  },
  {
    artifactKind: 'flatgeobuf',
    label: 'Vector — FlatGeobuf (.fgb)',
    placeholder: 'https://host/path/to/features.fgb',
    slot: 'featureLayers',
  },
  {
    artifactKind: 'pmtiles',
    label: 'Vector — PMTiles (.pmtiles)',
    placeholder: 'https://host/path/to/features.pmtiles',
    slot: 'featureLayers',
  },
  {
    artifactKind: 'pmtiles-raster',
    label: 'Raster — PMTiles raster (.pmtiles)',
    placeholder: 'https://host/path/to/raster.pmtiles',
    slot: 'realityData',
  },
  {
    artifactKind: 'geoparquet',
    label: 'Vector — GeoParquet (.parquet)',
    placeholder: 'https://host/path/to/features.parquet',
    slot: 'featureLayers',
  },
  {
    artifactKind: 'dxf',
    label: 'Vector — DXF',
    placeholder: 'https://host/path/to/drawing.dxf',
    slot: 'featureLayers',
  },
];
